import { useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { RootState } from '@/core/store'
import { closePlayer } from '@/core/store/slices/globalPlayer'

/**
 * A hook that closes the global player when navigating away from specific routes.
 *
 * @param routes - Array of route patterns that should keep the player open
 * @returns void
 */
export const useRoutePlayerControl = (keepPlayerRoutes: string[] = []) => {
	const location = useLocation()
	const dispatch = useDispatch()
	const { isOpen } = useSelector((state: RootState) => state.globalPlayer)

	useEffect(() => {
		// Don't do anything if the player isn't open
		if (!isOpen) return

		// Check if player has been marked for persistence across navigation
		const persistOnNavigation =
			localStorage.getItem('player_persist_navigation') === 'true'

		// If the content is marked to persist on navigation, don't close it
		if (persistOnNavigation) return

		// Check if current path should keep the player open
		const shouldKeepOpen = keepPlayerRoutes.some((route) => {
			// Exact match
			if (route === location.pathname) return true

			// Pattern with wildcard, e.g. '/zones/*'
			if (route.endsWith('/*')) {
				const baseRoute = route.slice(0, -2)
				return location.pathname.startsWith(baseRoute)
			}

			// Pattern with parameter, e.g. '/zones/:zoneId'
			if (route.includes(':')) {
				const routeParts = route.split('/')
				const pathParts = location.pathname.split('/')

				if (routeParts.length !== pathParts.length) return false

				return routeParts.every((part, index) => {
					// Parameter part (starts with :)
					if (part.startsWith(':')) return true
					// Exact match part
					return part === pathParts[index]
				})
			}

			return false
		})

		// Close the player if we're not on a route that should keep it open
		if (!shouldKeepOpen) {
			dispatch(closePlayer())
		}
	}, [location.pathname, isOpen, dispatch, keepPlayerRoutes])
}

export default useRoutePlayerControl
